import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "@/store";
import { fetchCurrentUser, clearAuth } from "@/store/authSlice";
import { useNotification } from "@/context/NotificationContext";
import { extractErrorMessage } from "@/api/axios";
import * as usersApi from "@/api/users.api";
import { CenteredContainer } from "@/components/CenteredContainer";
import { documentTitlePage } from "@/constants/app";
import { hr } from "@/constants/strings.hr";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { notifyIfUsernameInvalid } from "@/utils/username";

export function ProfilePage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, loading } = useAppSelector((s) => s.auth);
  const { notify } = useNotification();
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useDocumentTitle(documentTitlePage(hr.profile.title));

  useEffect(() => {
    if (user) setUsername(user.username);
  }, [user]);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/");
  }, [loading, isAuthenticated, navigate]);

  if (!user) return null;

  const handleSave = async () => {
    if (!notifyIfUsernameInvalid(username, notify)) return;
    setSaving(true);
    try {
      await usersApi.updateMyUsername(username);
      dispatch(fetchCurrentUser());
      notify(hr.profile.usernameUpdated, "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await usersApi.deleteMyAccount();
      dispatch(clearAuth());
      notify(hr.profile.deleted, "success");
      navigate("/");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
    setDeleteOpen(false);
  };

  return (
    <CenteredContainer maxWidth={500}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {hr.profile.title}
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 0.5,
            }}
          >
            {hr.profile.emailLabel}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {user.email}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 0.5,
            }}
          >
            {hr.profile.usernameLabel}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || username === user.username}
            >
              {hr.common.save}
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {hr.profile.createdLabel}:{" "}
            {new Date(user.createdAt).toLocaleDateString("hr-HR")}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setDeleteOpen(true)}
          >
            {hr.profile.deleteProfile}
          </Button>
        </CardContent>
      </Card>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{hr.profile.deleteDialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{hr.profile.deleteDialogBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>
            {hr.profile.cancel}
          </Button>
          <Button onClick={handleDelete} color="error">
            {hr.profile.confirmDelete}
          </Button>
        </DialogActions>
      </Dialog>
    </CenteredContainer>
  );
}
